import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  Suspense,
  useRef,
} from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { border, fontFamily, width } from "@mui/system";
import StripeCardShimmer from "./StripeCardShimmer";

import "../css/stripe.css";

// import "./stripe.css";

const CheckoutForm = forwardRef(
  (
    {
      setErrorMessage,
      setShowErrorAlert,
      setSendingData,
      setStripeCardPaymentFunction,
      defaultPaymentProcessor,
      processStripeCard = undefined, // create payment wont use this. instead will use returned value from handleSubmit
      stripeAccountID,
      setSendingDataWithCallback = undefined,
    },
    ref
  ) => {
    const stripe = useStripe();
    const elements = useElements();

    const [resetKey, setResetKey] = useState(0);
    const [zip, setZip] = useState("");

    const [cardNumberReady, setCardNumberReady] = useState(false);
    const [cardExpiryReady, setCardExpiryReady] = useState(false);
    const [cardCvcReady, setCardCvcReady] = useState(false);

    // Add new state for field validation
    const [fieldErrors, setFieldErrors] = useState({
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
      zip: false,
    });

    // Add a function to reset the form
    const resetForm = () => {
      setZip("");
      setResetKey((prevKey) => prevKey + 1);
      setFieldErrors({
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
        zip: false,
      });
    };

    const handleFieldFocus = (fieldName) => {
      setFieldErrors((prev) => ({
        ...prev,
        [fieldName]: false,
      }));
    };

    useImperativeHandle(ref, () => ({
      resetForm,
      handleSubmit: async () => {
        setSendingData(true);
        // event.preventDefault();
        // const zipValue = document.getElementById("cs-zip").value;
        // let billingDetails = {};
        // if (zipValue) {

        // }

        if (!stripe || !elements) {
          // Stripe.js has not yet loaded.
          return;
        }

        // Get all card elements
        const cardNumber = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        // Collect validation errors
        const validationErrors = [];
        if (!cardNumber._complete) validationErrors.push(`CARD_NUMBER_ERROR`);
        if (!cardExpiry._complete) validationErrors.push(`EXPIRY_ERROR`);
        if (!cardCvc._complete) validationErrors.push(`CVC_ERROR`);
        if (!zip) validationErrors.push(`ZIP_ERROR`);

        const newFieldErrors = {
          cardNumber: !cardNumber._complete,
          cardExpiry: !cardExpiry._complete,
          cardCvc: !cardCvc._complete,
          zip: !zip,
        };
        setFieldErrors(newFieldErrors);

        if (validationErrors.length > 0) {
          const errorMessage = validationErrors.join(", ");
          setErrorMessage(errorMessage);
          setShowErrorAlert(true);
          setSendingData(false);
          return { success: false, errors: validationErrors };
        }

        const billingDetails = {
          address: {
            postal_code: zip,
          },
        };

        const cardElement = elements.getElement(CardNumberElement);

        const { error, paymentMethod } = await stripe.createPaymentMethod(
          {
            type: "card",
            card: cardElement,
            billing_details: billingDetails,
          },
          {
            stripeAccount: stripeAccountID, // Include the connected account ID here
          }
        );

        if (error) {
          console.error("[error]", error);
          setErrorMessage(error.message);
          setShowErrorAlert(true);
          setSendingData(false);
          return { success: false, errors: ["CARD_NUMBER_ERROR"] };
        }

        setErrorMessage("");
        setShowErrorAlert(false);
        // setSendingData(false);
        if (processStripeCard) {
          processStripeCard(paymentMethod);
        }
        if (setSendingDataWithCallback) {
          setSendingDataWithCallback(false, () => {});
        }
        return { success: true, paymentMethod };
      },
    }));

    // if (!stripeInstance) {
    //   return <div>Loading...</div>; // Or any loading indicator
    // }

    const showShimmer = !cardNumberReady || !cardExpiryReady || !cardCvcReady;

    // Update the border classes based on error state
    const getErrorBorderClass = (fieldName) =>
      fieldErrors[fieldName] ? "border-red-500" : "border-edge-input";

    // Helper function to get error message for each field
    const getErrorMessage = (fieldName) => {
      switch (fieldName) {
        case "cardNumber":
          return "Please enter a valid card number";
        case "cardCvc":
          return "Invalid CVV";
        case "cardExpiry":
          return "Invalid expiration date";
        case "zip":
          return "Invalid zip/postal code";
        default:
          return "";
      }
    };

    return (
      <>
        <div className="w-full" style={showShimmer ? {} : { display: "none" }}>
          <StripeCardShimmer />
        </div>
        <div
          className="w-full"
          key={resetKey}
          style={showShimmer ? { display: "none" } : {}}
        >
          {/* Card Number Field */}
          <label className="block  text-sm text-ink-muted">Card Number</label>
          <div
            className={`mb-2.5 h-7 rounded-md border ${getErrorBorderClass(
              "cardNumber"
            )} bg-white px-3`}
          >
            <CardNumberElement
              onReady={() => setCardNumberReady(true)}
              onFocus={() => handleFieldFocus("cardNumber")}
              options={{
                placeholder: "",
                showIcon: false,
                style: {
                  base: {
                    fontSize: "14px",
                    lineHeight: "28px",
                    color: "#3A3429",
                    fontFamily: "Inter, system-ui, sans-serif",
                    padding: "0px",
                    "::placeholder": {
                      color: "#9A9285",
                    },
                    ":focus": {
                      color: "#3A3429",
                    },
                    ":-webkit-autofill": {
                      "-webkit-text-fill-color": "#3A3429",
                      "-webkit-box-shadow": "0 0 0px 1000px #FFFFFF inset",
                    },
                  },
                  invalid: {
                    color: "#EF4444",
                  },
                },
              }}
            />
          </div>
          {fieldErrors.cardNumber && (
            <p className="mb-2.5 text-xs text-red-500">
              {getErrorMessage("cardNumber")}
            </p>
          )}

          {/* Row container for expiry, CVV, and zip */}
          <div className="flex gap-2.5">
            {/* Expiry Field */}

            {/* CVC Field */}
            <div className="flex-1">
              <label className="block  text-sm text-ink-muted">CVV</label>
              <div
                className={`h-7 rounded-md border ${getErrorBorderClass(
                  "cardCvc"
                )} bg-white px-3`}
              >
                <CardCvcElement
                  onReady={() => setCardCvcReady(true)}
                  onFocus={() => handleFieldFocus("cardCvc")}
                  options={{
                    placeholder: "",
                    style: {
                      base: {
                        fontSize: "14px",
                        lineHeight: "28px",
                        color: "#3A3429",
                        fontFamily: "Inter, system-ui, sans-serif",
                        padding: "0px",
                        "::placeholder": {
                          color: "#9A9285",
                        },
                      },
                    },
                  }}
                />
              </div>
              {fieldErrors.cardCvc && (
                <p className="mt-1 text-xs text-red-500">
                  {getErrorMessage("cardCvc")}
                </p>
              )}
            </div>

            <div className="flex-1">
              <label className="block  text-sm text-ink-muted">
                Expiration
              </label>
              <div
                className={`h-7 rounded-md border ${getErrorBorderClass(
                  "cardExpiry"
                )} bg-white px-3`}
              >
                <CardExpiryElement
                  onReady={() => setCardExpiryReady(true)}
                  onFocus={() => handleFieldFocus("cardExpiry")}
                  options={{
                    placeholder: "",
                    style: {
                      base: {
                        fontSize: "14px",
                        lineHeight: "28px",
                        color: "#3A3429",
                        fontFamily: "Inter, system-ui, sans-serif",
                        padding: "0px",
                        "::placeholder": {
                          color: "#9A9285",
                        },
                      },
                    },
                  }}
                />
              </div>
              {fieldErrors.cardExpiry && (
                <p className="mt-1 text-xs text-red-500">
                  {getErrorMessage("cardExpiry")}
                </p>
              )}
            </div>

            {/* Zip Field */}
            <div className="flex-1">
              <label className="block  text-sm text-ink-muted">
                Zip/Postal
              </label>
              <div
                className={`relative h-7 rounded-md border ${getErrorBorderClass(
                  "zip"
                )} bg-white`}
              >
                <input
                  onFocus={() => handleFieldFocus("zip")}
                  id="cs-zip"
                  type="text"
                  maxLength="10"
                  className="h-full w-full rounded-md border-none bg-transparent px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-muted"
                  value={zip}
                  onChange={(event) => {
                    let value = event.target.value;
                    // value = value.replace(/[^A-Za-z0-9]+/g, "");
                    setZip(value);
                  }}
                  autoComplete="postal-code"
                />
              </div>
              {fieldErrors.zip && (
                <p className="mt-1 text-xs text-red-500">
                  {getErrorMessage("zip")}
                </p>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

const StripeCheckout = forwardRef(
  (
    {
      setErrorMessage,
      setShowErrorAlert,
      setSendingData,
      defaultPaymentProcessor,
      processStripeCard,
      stripeAccountID,
      setSendingDataWithCallback = undefined,
    },
    ref
  ) => {
    // const [stripePromise, setStripePromise] = useState(null);

    // useEffect(() => {
    //   if (stripeAccountID) {
    //     const stripeInstance = loadStripe(
    //       "pk_test_51Lb9EUFFzBBowpWlLZ1J6uBUdmrBwnKnfmyjgD9E4DlInlVLNaVzXeexqlExKJLkIX3jz9slzXJa4N5ZwRcxffDr00ngy13lmA",
    //       {
    //         stripeAccount: stripeAccountID,
    //       }
    //     );
    //     setStripePromise(stripeInstance);
    //   }
    // }, [stripeAccountID]);

    // if (!stripePromise) {
    //   return <div>Loading...</div>; // Or any loading indicator
    // }
    return (
      // <Elements stripe={stripePromise} width="100%">
      <CheckoutForm
        ref={ref}
        width="100%"
        setErrorMessage={setErrorMessage}
        setShowErrorAlert={setShowErrorAlert}
        setSendingData={setSendingData}
        defaultPaymentProcessor={defaultPaymentProcessor}
        processStripeCard={processStripeCard}
        stripeAccountID={stripeAccountID}
        setSendingDataWithCallback={setSendingDataWithCallback}
      />
      // </Elements>
    );
  }
);

CheckoutForm.displayName = "CheckoutForm";
StripeCheckout.displayName = "StripeCheckout";

export default StripeCheckout;
